import { Link } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { SectionContainer, AnimatedHeading, GlassCard, TechTag } from '../components/ui';
import {
  ScrollProgress, Reveal, WordReveal, TiltCard, HoverIcon, Parallax,
  ScanSweep, ChapterLine, HeroBackdrop,
} from '../components/motion/ScrollMotion';

const TECH = [
  {
    name: 'Radare2', icon: '◈', color: '#00f0ff',
    desc: 'Open-source reverse engineering framework. Provides rapid binary analysis, function detection, and cross-reference graph traversal. Used as the primary extraction engine.',
    features: ['Function detection', 'String extraction', 'Import/export parsing', 'Cross-reference analysis'],
  },
  {
    name: 'Ghidra', icon: '⬡', color: '#00c8ff',
    desc: 'NSA-developed open-source decompiler. When available, Ghidra provides deep decompilation with symbol recovery and high-level pseudocode generation.',
    features: ['Optional engine', 'Symbol recovery', 'Pseudocode output', 'Not on the hosted free tier'],
  },
  {
    name: 'CAPA', icon: '◆', color: '#ff8c42',
    desc: "FireEye/Mandiant's capability analysis tool. Applies over 1,000 YAML rules to identify behavioral capabilities in PE and ELF binaries. It can be disabled on low-memory hosts; reports then say so.",
    features: ['1,000+ behavioral rules', 'Namespace taxonomy', 'PE and ELF', 'Optional on small hosts'],
  },
  {
    name: 'MITRE ATT&CK', icon: '◉', color: '#ffb347',
    desc: 'The industry-standard adversarial tactics and techniques framework. ARISE maps detected capabilities to candidate ATT&CK technique IDs for analyst review.',
    features: ['Candidate technique IDs', 'Analyst review', 'Technique + sub-technique', 'Tactic phase labeling'],
  },
];

/* Decorative hero chips: names of tools that already appear on this page. */
const HERO_LABELS = [
  { text: 'Radare2', pos: { left: '5%', top: '24%' } },
  { text: 'Ghidra', pos: { left: '11%', top: '64%' } },
  { text: 'CAPA', pos: { right: '6%', top: '30%' } },
  { text: 'MITRE ATT&CK', pos: { right: '10%', top: '68%' } },
];

const ARCH_FRONTEND = ['React 18', 'Framer Motion', 'Recharts', 'React Router'];
const ARCH_ANALYSIS = ['Radare2', 'Ghidra', 'CAPA Rules', 'MITRE ATT&CK', 'Heuristic Scoring'];

function TechCard({ tech, index }) {
  return (
    // Parallax (outer) → Reveal (entrance) → TiltCard (hover) are separate
    // layers so their transforms never fight each other.
    <Parallax distance={index % 2 === 0 ? 8 : 14}>
      <Reveal delay={(index % 2) * 0.12} y={36} blur={5} style={{ height: '100%' }}>
        <TiltCard color={tech.color} fill>
          <GlassCard glow color={tech.color} style={{ padding: '2rem', height: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', marginBottom: '1rem' }}>
              <HoverIcon
                color={tech.color}
                style={{
                  width: 46, height: 46, borderRadius: 10, flexShrink: 0,
                  background: `${tech.color}15`, border: `1px solid ${tech.color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 20, color: tech.color,
                }}
              >
                {tech.icon}
              </HoverIcon>
              <h3 style={{ fontFamily: 'Orbitron', fontSize: 'clamp(14px,2vw,18px)', fontWeight: 700, color: tech.color, letterSpacing: '0.04em' }}>
                {tech.name}
              </h3>
            </div>
            <p style={{ fontFamily: 'Inter', fontSize: 13, color: '#6b8fa8', lineHeight: 1.7, marginBottom: '1rem' }}>
              {tech.desc}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
              {tech.features.map(f => (
                <span key={f} style={{
                  fontFamily: 'Space Mono', fontSize: 9, color: tech.color,
                  border: `1px solid ${tech.color}25`, borderRadius: 3,
                  padding: '3px 8px', letterSpacing: 0.5,
                  background: `${tech.color}06`,
                }}>
                  {f}
                </span>
              ))}
            </div>
          </GlassCard>
        </TiltCard>
      </Reveal>
    </Parallax>
  );
}

const ARCH_LABEL = { fontFamily: 'Space Mono', fontSize: 9, color: '#6b8fa8', letterSpacing: 2, textTransform: 'uppercase', marginBottom: '1rem' };
const ARCH_ROW = { fontFamily: 'Inter', fontSize: 13, color: '#a0c4d8', padding: '6px 0', borderBottom: '1px solid rgba(0,240,255,0.06)' };

function AboutContent() {
  return (
    <div className="page-wrapper" style={{ paddingTop: 64 }}>
      <ScrollProgress />
      <SectionContainer as="section">
        {/* Hero: layered parallax backdrop (page-local; global background untouched) */}
        <div style={{ position: 'relative', overflowX: 'clip' }}>
          <HeroBackdrop variant="rings" labels={HERO_LABELS} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <AnimatedHeading
              center
              subtitle="ARISE (Advanced Real-Time Intelligence for Security Evaluation) is a next-generation malware analysis platform designed for security professionals."
              level={1}
            >
              <WordReveal text="About " style={{ color: '#e0f4ff' }} />
              <WordReveal text="ARISE" className="neon-text" delay={0.35} />
            </AnimatedHeading>
          </div>
        </div>

        {/* Mission */}
        <Reveal y={36} scale={0.97}>
          <GlassCard glow style={{ padding: 'clamp(1.75rem,4vw,3rem)', marginBottom: '3.5rem', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Space Mono', fontSize: 9, color: '#6b8fa8', letterSpacing: 3, textTransform: 'uppercase', marginBottom: '1rem' }}>
              Mission
            </div>
            <h2 style={{ fontFamily: 'Orbitron', fontSize: 'clamp(1.25rem,3vw,2.25rem)', fontWeight: 700, color: '#e0f4ff', lineHeight: 1.2, marginBottom: '1.25rem', letterSpacing: '0.04em' }}>
              <WordReveal inView text="Make elite-grade malware analysis" /><br />
              <WordReveal
                inView
                delay={0.45}
                text="accessible to every analyst."
                style={{ color: '#00f0ff', textShadow: '0 0 28px rgba(0,240,255,0.38)' }}
              />
            </h2>
            <Reveal y={16} blur={0} delay={0.3}>
              <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#6b8fa8', lineHeight: 1.8, maxWidth: 660, margin: '0 auto' }}>
                Traditional malware analysis requires deep expertise across multiple tools — Radare2, Ghidra, CAPA, and MITRE frameworks —
                each with its own learning curve. ARISE unifies them into a single pipeline with AI-powered interpretation,
                so analysts can focus on decisions, not tooling.
              </p>
            </Reveal>
          </GlassCard>
        </Reveal>

        <ChapterLine />

        {/* Architecture */}
        <AnimatedHeading subtitle="How the platform components connect and communicate." level={2}>
          <WordReveal inView text="System " style={{ color: '#e0f4ff' }} />
          <WordReveal inView text="Architecture" className="neon-text" delay={0.3} />
        </AnimatedHeading>

        {/* Wrapper clips the one-shot scan sweep and owns the section gap */}
        <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 8, marginBottom: '3.5rem' }}>
          <Reveal y={32} scale={0.98}>
            <GlassCard style={{ padding: 'clamp(1.5rem,3vw,2.5rem)' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
                gap: 'clamp(1rem,3vw,2rem)',
                alignItems: 'start',
              }}
                className="arise-arch-grid"
              >
                {/* Frontend */}
                <Reveal delay={0.1} y={22} blur={0} style={{ textAlign: 'center' }}>
                  <div style={ARCH_LABEL}>Frontend</div>
                  {ARCH_FRONTEND.map(t => (
                    <div key={t} style={ARCH_ROW}>{t}</div>
                  ))}
                </Reveal>

                {/* API layer */}
                <Reveal delay={0.22} y={22} blur={0} style={{ textAlign: 'center' }}>
                  <div style={ARCH_LABEL}>API Layer</div>
                  <GlassCard glow style={{ padding: '1.25rem', textAlign: 'center', marginBottom: '0.75rem' }}>
                    <div style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 15, color: '#00f0ff', letterSpacing: 1, marginBottom: '0.4rem' }}>
                      FastAPI
                    </div>
                    <div style={{ fontFamily: 'Space Mono', fontSize: 9, color: '#6b8fa8', lineHeight: 1.8 }}>
                      POST /upload<br />
                      GET /jobs/:id<br />
                      GET /analysis/:id<br />
                      GET /health
                    </div>
                  </GlassCard>
                  <div style={{ fontFamily: 'Space Mono', fontSize: 9, color: '#6b8fa8', letterSpacing: 1, textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                    ↕ In-process job queue
                  </div>
                  <GlassCard style={{ padding: '0.9rem' }}>
                    <div style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: 13, color: '#a0c4d8', textAlign: 'center' }}>
                      Job Runner
                    </div>
                    <div style={{ fontFamily: 'Space Mono', fontSize: 9, color: '#6b8fa8', textAlign: 'center', marginTop: '0.2rem' }}>
                      Background tasks
                    </div>
                  </GlassCard>
                </Reveal>

                {/* Analysis */}
                <Reveal delay={0.34} y={22} blur={0} style={{ textAlign: 'center' }}>
                  <div style={ARCH_LABEL}>Analysis</div>
                  {ARCH_ANALYSIS.map(t => (
                    <div key={t} style={ARCH_ROW}>{t}</div>
                  ))}
                </Reveal>
              </div>
            </GlassCard>
          </Reveal>
          <ScanSweep />
        </div>

        <ChapterLine />

        {/* Tech stack */}
        <AnimatedHeading subtitle="The open-source security tools powering every analysis." level={2}>
          <WordReveal inView text="Technology " style={{ color: '#e0f4ff' }} />
          <WordReveal inView text="Stack" className="neon-text" delay={0.3} />
        </AnimatedHeading>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
          gap: '1.25rem',
          alignItems: 'stretch',
        }}
          className="arise-tech-grid"
        >
          {TECH.map((t, i) => (
            <TechCard key={t.name} tech={t} index={i} />
          ))}
        </div>

        {/* Closing call to action (navigation only; routes already exist in the navbar) */}
        <ChapterLine style={{ marginTop: '3.5rem' }} />
        <Reveal y={36} scale={0.97}>
          <GlassCard glow style={{ padding: 'clamp(1.75rem,4vw,3rem)', textAlign: 'center' }}>
            <h2 style={{ fontFamily: 'Orbitron', fontSize: 'clamp(1.1rem,2.5vw,1.75rem)', fontWeight: 700, color: '#e0f4ff', lineHeight: 1.25, marginBottom: '0.75rem', letterSpacing: '0.04em' }}>
              See the pipeline in action
            </h2>
            <p style={{ fontFamily: 'Inter', fontSize: 14, color: '#6b8fa8', lineHeight: 1.7, maxWidth: 520, margin: '0 auto 1.5rem' }}>
              Analyze a binary, or walk through the analysis stages one by one.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.75rem' }}>
              <Link to="/analyze" className="btn-primary">Analyze File</Link>
              <Link to="/how-it-works" className="btn-ghost">How It Works</Link>
            </div>
          </GlassCard>
        </Reveal>
      </SectionContainer>

      <style>{`
        @media (max-width: 680px) {
          .arise-arch-grid { grid-template-columns: 1fr !important; }
          .arise-tech-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

/* reducedMotion="user": any framer animation on this page skips transform
 * movement when the OS asks for reduced motion. */
export default function AboutPage() {
  return (
    <MotionConfig reducedMotion="user">
      <AboutContent />
    </MotionConfig>
  );
}
